import { isEmpty, round } from 'lodash'
import { dayjs, utils, occupancyOverrides, scheduleLibrary } from 'passiv-libs'

const _getAirTemperature = obj => (isEmpty(obj) ? '' : `${round(obj.value, 1)}°`)
const _getAirTemperatureAriaLabel = obj => (isEmpty(obj) ? '. Air temperature unknown' : ` at ${round(obj.value, 1)} degrees`)

export const getInitialUntilTime = ({ occupancy }) => {
  const time = occupancy[0].type === 'OUT'
    ? dayjs(occupancy[0].until_time).utc()
    : dayjs().add(2, 'hours').utc()

  return time.minute(Math.round(time.minute() / 5) * 5).format()
}

export const convertTimeToUTCDate = time => {
  const [hours, mins] = time.split(':')
  return dayjs().hour(hours).minute(mins).utc().format()
}

export const getAwayTileContent = occupancyAway => {
  const isAwayNow = utils.heating.isAwayNow({ ...occupancyAway })
  return isEmpty(occupancyAway)
    ? { type: 'not-set',
        label: 'Going Away' 
      }
    : {
        type: isAwayNow ? 'now' : 'future',
        label: isAwayNow ? 'Away Until' : 'Going Away', 
        day: isAwayNow
          ? dayjs(occupancyAway.end).format('D')
          : dayjs(occupancyAway.start).format('D'),
        month: isAwayNow
          ? dayjs(occupancyAway.end).format('MMM')
          : dayjs(occupancyAway.start).format('MMM')
      }
}

// todo: if more than 9 tiles?
export const getLinks = ({
  offline,
  windowWidth,
  messagesBadge,
  alerts = [],
  zones = [],
  groups = [],
  hotwater = {},
  schedule = {},
  occupancy_away = {},
  heating = {},
  awayTile = {},
  allHeatingOccupancies
}) => {
  const WIDTH = 985 // NB: taken from Tree UI

  const isAwayNow = utils.heating.isAwayNow({
    start: occupancy_away.start,
    end: occupancy_away.end
  })

  const arr = []

  // Add in-out toggle tile
  if (utils.account.hasCapability('immersionhotwater') || utils.account.hasCapability('heating')) {
    if (utils.account.hasCapability('multizone')) {
      const occupancy = occupancyOverrides.mz.getWholeHomeOccupancy(allHeatingOccupancies)
      arr.push({
        dataContainer: 'tile-in-out',
        id: 'in-out',
        disabled: offline,
        name: 'I am...',
        ariaLabel: `I am. ${occupancy[0].type === 'IN' || occupancy[0].type === 'ASLEEP' ? 'home' : occupancy[0].type === 'AWAY' ? 'away' : 'out'}`,
        icon: 'in-out',
        link: '/',
        occupancy
      })
    } else {
      arr.push({
        dataContainer: 'tile-in-out',
        id: 'in-out',
        disabled: offline,
        name: 'I am...',
        ariaLabel: `I am. ${heating.zone1.occupancy[0].type === 'IN' || heating.zone1.occupancy[0].type === 'ASLEEP' ? 'home' : heating.zone1.occupancy[0].type === 'AWAY' ? 'away' : 'out'}`,
        icon: 'in-out',
        link: '/',
        occupancy: heating.zone1.occupancy
      })
    }
  }

  // Add heating tile
  if (utils.account.hasCapability('heating')) {
    if (utils.account.hasCapability('multizone')) {
      if (zones.length === 0) {
        const emptyZone = { dataContainer: 'tile-heating-1', id: 'heating', name: '', icon: 'heating', disabled: offline }
        arr.push(emptyZone)
        arr.push(emptyZone)
      } else {
        zones
          .filter(({ type }) => type === 'heating')
          .forEach(({ id }) => {
            const zone = utils.multizone.getZoneFromId({ zoneID: id, zones })
            const name = (zones.filter(({ type }) => type === 'heating').length > 1) ? zone?.friendlyName || zone?.zoneName : "Heating"
            arr.push({
              dataContainer: `tile-heating-${id}`,
              id: 'heating',
              disabled: (offline || isAwayNow),
              name,
              icon: 'heating',
              link: `/heating/zone/${id}`,
              occupancy: heating[`zone${id}`].occupancy,
              airTemperature: _getAirTemperature(heating[`zone${id}`].air_temperature),
              airTemperatureAriaLabel: _getAirTemperatureAriaLabel(heating[`zone${id}`].air_temperature)              
            })
          })
      }
    } else {
      arr.push({
        dataContainer: 'tile-heating-1',
        id: 'heating',
        disabled: (offline || isAwayNow),
        name: 'Heating',
        icon: 'heating',
        link: '/heating/zone/1',
        occupancy: heating.zone1.occupancy,
        airTemperature: _getAirTemperature(heating.zone1.air_temperature),
        airTemperatureAriaLabel: _getAirTemperatureAriaLabel(heating.zone1.air_temperature)  
      })
    }
  }

  // Add hot water tile
  if (utils.account.hasCapability('hotwater') || utils.account.hasCapability('immersionhotwater')) {
    const hwId = zones.find(({ type }) => type === 'hot_water')?.id
    
    let colour = 'in-red'
    if (hotwater.settings.controllerMode === 'MANUAL') {
      colour = 'out-grey'
    }
    else if (isAwayNow) {
      colour = 'away-grey'
    }
    else if (utils.hotwater.hasOwnSchedule({ groups, zones })) {
      const allPeriods = schedule[`zone${hwId}`]?.allPeriods
      const occupancy = allPeriods
        ? utils.schedule.getCurrentDefaultOccupancy({ allPeriods })
        : 'occupied'
      colour = (occupancy === 'asleep' || occupancy === 'unoccupied') ? 'out-grey' : 'in-red'
    } else {
      const zoneId = utils.hotwater.getZoneSibling({ groups, zones })?.id || 1
      const occupancy = heating[`zone${zoneId}`]?.occupancy[0].type || 'occupied'
      colour = (occupancy === 'ASLEEP' || occupancy === 'OUT') ? 'out-grey' : 'in-red'
    }

    arr.push({
      dataContainer: 'tile-hot-water',
      id: 'hot-water',
      disabled: offline || isAwayNow,
      name: 'Hot Water',
      colour: colour,
      icon: 'tile-hot-water',
      link: '/hotwater'
    })
  }

  // Add messages and alerts tile
  if (alerts.length > 0) {
    arr.push({
      dataContainer: 'tile-messages-alerts',
      id: 'alerts',
      disabled: offline,
      ariaLabel: `${(alerts.length > 0) ? alerts.length : ''} alerts`,
      name: 'Alerts',
      icon: 'tile-alerts',
      link: '/messages-and-alerts/view/alerts',
      badge: alerts.length
    })
  } else {
    arr.push({
      dataContainer: 'tile-messages-alerts',
      id: 'messages-alerts',
      disabled: offline,
      ariaLabel: `Messages & Alerts. ${(messagesBadge > 0) 
          ? `${messagesBadge} unread message${(messagesBadge > 1) ? 's' : ''}` 
          : ''}`,
      name: 'Messages & Alerts',
      colour: isAwayNow ? 'away-grey' : 'blue',
      icon: 'tile-messages-and-alerts',
      link: '/messages-and-alerts/view/messages',
      badge: messagesBadge
    })
  }

  // Add MMSP tile
  if (windowWidth > WIDTH && utils.account.hasCapability('mmsp')) {
    arr.push({
      dataContainer: 'tile-mmsp',
      id: 'charts',
      disabled: false,
      name: 'MMSP',
      icon: 'tile-charts-line',
      link: '/charts/mmsp'
    })
  }

  // Add solar tile
  if (windowWidth > WIDTH && utils.account.hasCapability('solar')) {
    arr.push({
      dataContainer: 'tile-solar',
      id: 'charts',
      disabled: false,
      name: 'Solar',
      icon: 'tile-charts-line',
      link: '/charts/solar'
    })
  }

  // Add historical analysis tile
  if (
    windowWidth > WIDTH &&
    utils.account.hasCapability('heating') &&
    utils.account.getHeatingType() !== 'HeatPump' &&
    utils.account.getHeatingType() !== 'HybridHeatPump'
  ) {
    arr.push({
      dataContainer: 'tile-historical-analysis',
      id: 'charts',
      disabled: false,
      name: 'Historical Analysis',
      icon: 'tile-charts',
      link: '/charts/historical-analysis'
    })
  }

  // Add 24 hour analysis tile
  if (utils.account.hasCapability('energygraph')) {
    arr.push({
      dataContainer: 'tile-historical-24-hour-analysis',
      id: 'charts',
      disabled: false,
      name: '24 Hour Analysis',
      icon: 'tile-charts',
      link: '/charts/graphs'
    })
  }

  // Add away tile
  if (utils.account.hasCapability('immersionhotwater') || utils.account.hasCapability('heating')) {
    arr.push({
      dataContainer: 'tile-away',
      id: 'away',
      disabled: offline,
      name: awayTile.type === 'now' ? 'Away Until' : 'Going Away',
      icon: awayTile.type === 'not-set' ? 'tile-away-not-set' : 'tile-away-tile',
      awayTile,
      link: '/',
      occupancy: heating.zone1.occupancy
    })
  }

  arr.push(
    // { id: 'battery', disabled: false, name: 'Battery', icon: 'tile-battery', link: '/' },
    // { id: 'electric-vehicle', disabled: false, name: 'Electric Vehicle', icon: 'tile-hot-water', link: '/' },
    {
      dataContainer: 'tile-account-settings',
      id: 'account-settings',
      colour: isAwayNow ? 'away-grey' : 'blue',
      disabled: false,
      name: 'Account & Settings',
      icon: 'account',
      link: '/account-and-settings'
    }
  )
  return arr
}

export const getTipModalText = ({
  type,
  asleepSetpoint,
  zones,
  occupancyStr,
  heating,
  schedule,
  untilTime
}) => {
  if (type === 'OUT-TO-ASLEEP') {
    return utils.account.isMultiHeatingZone(({ zones }))
      ? 'If your schedule is currently ‘Asleep’ we’ve set your thermostat to your ‘Asleep’ temperature.'
      : `As your schedule is 'Asleep' we've set your thermostat to your 'Asleep' temperature of ${asleepSetpoint}°C.`
  }

  // multizone
  if (utils.account.isMultiHeatingZone({ zones })) {
    const _in = utils.temperatures.getInString()
    const _zones = zones.filter(({ type }) => type === 'heating')

    if (occupancyStr === 'IN') {
      let text = "We have updated your "
      _zones.forEach((zone, i) => {
        const name = (zone.friendlyName && zone.friendlyName.length > 0) ? zone.friendlyName : zone.zoneName
        const setpoint = heating[`zone${zone.id}`]?.default_setpoints[_in]?.value
        if (i == 0) {
          text = text + `‘${name}‘ desired temperature to ${setpoint}°`
        }
        else {
          text = text + ` and your ‘${name}‘ desired temperature to ${setpoint}°`
        }        
      })
      return text
    } else if (occupancyStr === 'OUT') {
      const untilTimeText = dayjs(untilTime).format('HH:mm')
      let text = `Your home won’t drop below its ‘Out’ setpoint.\nFor your return at ${untilTimeText} `
      _zones.forEach((zone, i) => {
        const name = (zone.friendlyName && zone.friendlyName.length > 0) ? zone.friendlyName : zone.zoneName
        const todaysPeriods = utils.heating.getTodaysSchedule(schedule[`zone${zone.id}`]?.allPeriods)
        const occupancy = scheduleLibrary.getPeriodAtTime({ periods: todaysPeriods, time: untilTimeText })?.occupancy
        text = text + ((i === 0) ? 'your ' : `.\nYour `)
        if (occupancy === "occupied") {
          const setpoint = heating[`zone${zone.id}`]?.default_setpoints[_in]?.value
          text = text + `‘${name}‘ will be warmed to your ‘In’ set point of ${setpoint}°`
        }
        else if (occupancy === "unoccupied") {
          const setpoint = heating[`zone${zone.id}`]?.default_setpoints['out']?.value
          text = text + `‘${name}‘  will return to your ‘Out’ set point of ${setpoint}°`          
        }
        else { // occupancy === "asleep"
          const setpoint = heating[`zone${zone.id}`]?.default_setpoints['asleep']?.value
          text = text + `‘${name}‘  will return to your ‘Asleep’ set point of ${setpoint}°`
        } 
      })
      return text
    } else {
      return ''
    }
  } else if (utils.account.hasCapability("immersionhotwater")) {
    // immersion hot water
    if (occupancyStr === 'IN') {
      return `Your hot water is now on.`
    } else if (occupancyStr === 'OUT') {
      return `Your hot water is now off`
    } else {
      return ''
    }
  } else {
    // singlezone
    const _in = utils.temperatures.getInString()

    // NB zigbee users may only have `in` rather than `in_am`
    const inSetpoint = heating.zone1.default_setpoints[_in].value
    const outSetpoint = heating.zone1.default_setpoints.out.value

    if (occupancyStr === 'IN') {
      return `We have updated your desired temperature ${inSetpoint}°.`
    } else if (occupancyStr === 'OUT') {
      const untilTimeText = dayjs(untilTime).format('HH:mm')
      return `Your home won’t drop below ${outSetpoint}°.\nFor your return at ${untilTimeText} your heating will be warmed to your ‘In’ set point of ${inSetpoint}°`
    } else {
      return ''
    }
  }
}

export const getMobiscrollValue = ({ occupancy }) => {
  const time = occupancy[0].type === 'OUT'
    ? dayjs(occupancy[0].until_time)
    : dayjs().add(2, 'hours')
  return time.minute(Math.round(time.minute() / 5) * 5).format('HH mm')
}

export const getTileWidth = ({ quantity, isLast, isLarge }) => {
  const _isOdd = value => value % 2 === 1
  const desktop = [4, 7, 10, 13].some(int => int === quantity)
    ? 'triple'
    : [5, 8, 11, 14].some(int => int === quantity)
    ? 'double'
    : 'single'
  const mobile = _isOdd(quantity) ? 'double' : 'single'
  return !isLast ? 'single' : isLarge ? desktop : mobile
}